import { useState, useEffect } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Table, { Column } from '../../components/Table';
import StatusBadge from '../../components/StatusBadge';
import LookupSelect, { LookupOption } from '../../components/LookupSelect';

interface Reader {
  id: string;
  reader_code: string;
  reader_type: string;
  reader_location: string;
  status: string;
  last_connection_date: string;
}

interface RawRow {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  clock_in_time: string;
  clock_out_time: string;
  clock_location: string;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function Readers() {
  const [readers, setReaders] = useState<Reader[]>([]);
  const [rawRows, setRawRows] = useState<RawRow[]>([]);
  const [newReader, setNewReader] = useState({ readerCode: '', readerType: 'Fingerprint', readerLocation: '' });
  const [filters, setFilters] = useState({ employeeId: '', fromDate: '', toDate: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReaders();
    fetchRaw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReaders = async () => {
    const res = await api.get('/tms/readers');
    setReaders(res.data.readers || []);
  };

  const fetchRaw = async (f = filters) => {
    setLoading(true);
    try {
      const params: any = {};
      if (f.employeeId) params.employeeId = f.employeeId;
      if (f.fromDate) params.fromDate = f.fromDate;
      if (f.toDate) params.toDate = f.toDate;
      const res = await api.get('/tms/raw-attendance', { params });
      setRawRows(res.data.rawAttendance || []);
    } finally {
      setLoading(false);
    }
  };

  const createReader = async () => {
    if (!newReader.readerCode) return alert('Reader code required');
    await api.post('/tms/readers', newReader);
    setNewReader({ readerCode: '', readerType: 'Fingerprint', readerLocation: '' });
    fetchReaders();
  };

  const syncReader = async (id: string) => {
    await api.post(`/tms/readers/${id}/sync`);
    fetchReaders();
  };

  const readerColumns: Column<Reader>[] = [
    { key: 'reader_code', header: 'Code' },
    { key: 'reader_type', header: 'Type' },
    { key: 'reader_location', header: 'Location' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'last_connection_date', header: 'Last Connection', render: (r) => (r.last_connection_date ? new Date(r.last_connection_date).toLocaleString() : '—') },
    { key: 'actions', header: '', render: (r) => <Button variant="secondary" onClick={() => syncReader(r.id)}>Sync</Button> },
  ];

  const rawColumns: Column<RawRow>[] = [
    { key: 'employee_code', header: 'Code' },
    { key: 'name', header: 'Employee', render: (r) => `${r.first_name} ${r.last_name}` },
    { key: 'clock_in_time', header: 'Clock In', render: (r) => (r.clock_in_time ? new Date(r.clock_in_time).toLocaleString() : '—') },
    { key: 'clock_out_time', header: 'Clock Out', render: (r) => (r.clock_out_time ? new Date(r.clock_out_time).toLocaleString() : '—') },
    { key: 'clock_location', header: 'Location' },
  ];

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Reader Status</h1>
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex gap-3 items-end flex-wrap">
          <Input label="Reader Code" value={newReader.readerCode} onChange={(e) => setNewReader({ ...newReader, readerCode: e.target.value })} />
          <Input label="Type" value={newReader.readerType} onChange={(e) => setNewReader({ ...newReader, readerType: e.target.value })} />
          <Input label="Location" value={newReader.readerLocation} onChange={(e) => setNewReader({ ...newReader, readerLocation: e.target.value })} />
          <Button onClick={createReader}>Add Reader</Button>
        </div>
        <Table columns={readerColumns} rows={readers} emptyMessage="No readers configured" />
      </div>

      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-4">Raw Attendance Viewer</h2>
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex gap-3 items-end flex-wrap">
          <LookupSelect label="Employee" value={filters.employeeId} onChange={(v) => setFilters({ ...filters, employeeId: v })} fetchOptions={fetchEmployeeOptions} placeholder="All employees" />
          <Input label="From Date" type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} />
          <Input label="To Date" type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} />
          <Button onClick={() => fetchRaw()}>Filter</Button>
        </div>
        <Table columns={rawColumns} rows={rawRows} loading={loading} emptyMessage="No raw attendance records" />
      </div>
    </div>
  );
}
